import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Check,
  ExternalLink,
  FolderTree,
  Link2,
  Plus,
  Search,
  Unlink,
} from 'lucide-react';

import { useCMSPageMenusQuery, cmsKeys } from '@/client/api/cms/cms.query';
import {
  useLinkCMSPageMenuMutation,
  useUnlinkCMSPageMenuMutation,
} from '@/client/api/cms/cms.mutation';
import {
  useCreateMenuNodeMutation,
} from '@/client/api/menus/menus.mutation';
import {
  useMenuListQuery,
  useMenuRootsQuery,
  useMenuTreeQuery,
} from '@/client/api/menus/menus.query';
import {
  FOOTER_MENU_ROOTS,
  MenuTreeNode,
} from '@/client/types/menus';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

type Mode = 'linked' | 'link' | 'create';

const rootLabel = (menid?: number | null) => {
  if (menid == null) return 'Unrooted';
  return FOOTER_MENU_ROOTS.find((r) => r.menid === menid)?.label ?? `Root #${menid}`;
};

const flattenTree = (nodes: MenuTreeNode[], depth = 0): { node: MenuTreeNode; depth: number }[] => {
  const out: { node: MenuTreeNode; depth: number }[] = [];
  for (const n of nodes) {
    out.push({ node: n, depth });
    if (n.children?.length) out.push(...flattenTree(n.children, depth + 1));
  }
  return out;
};

interface CMSMenuLinksPanelProps {
  cmsoid: number;
  pageTitle?: string;
  className?: string;
}

const CMSMenuLinksPanel = ({ cmsoid, pageTitle, className }: CMSMenuLinksPanelProps) => {
  const [mode, setMode] = useState<Mode>('linked');
  const [search, setSearch] = useState('');
  const [rootMenid, setRootMenid] = useState<number | ''>(FOOTER_MENU_ROOTS[0]?.menid ?? '');
  const [parentMenid, setParentMenid] = useState<number | ''>('');
  const [newName, setNewName] = useState(pageTitle ?? '');

  const menusQuery = useCMSPageMenusQuery(cmsoid);
  const rootsQuery = useMenuRootsQuery({ enabled: mode !== 'linked' });
  const treeQuery = useMenuTreeQuery({
    menid_root: typeof rootMenid === 'number' ? rootMenid : null,
    enabled: mode !== 'linked' && typeof rootMenid === 'number',
  });
  const searchQuery = useMenuListQuery(
    { q: search.trim() || undefined, limit: 40 },
    { enabled: mode === 'link' && search.trim().length >= 2 },
  );

  const linkMutation = useLinkCMSPageMenuMutation();
  const unlinkMutation = useUnlinkCMSPageMenuMutation();
  const createMutation = useCreateMenuNodeMutation();
  const qc = useQueryClient();

  const linked = menusQuery.data ?? [];
  const linkedIds = useMemo(() => new Set(linked.map((m) => m.menid)), [linked]);

  const flatTree = useMemo(
    () => flattenTree(treeQuery.data ?? []),
    [treeQuery.data],
  );

  const handleLink = async (menid: number, name?: string) => {
    try {
      await linkMutation.mutateAsync({ cmsoid, menid });
      toast.success(`Linked “${name || `#${menid}`}” to this page`);
      setMode('linked');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to link menu node');
    }
  };

  const handleUnlink = async (menid: number, name?: string) => {
    if (!window.confirm(`Unlink “${name || `#${menid}`}” from this page? The nav node stays.`)) {
      return;
    }
    try {
      await unlinkMutation.mutateAsync({ cmsoid, menid });
      toast.success('Menu node unlinked');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to unlink');
    }
  };

  const handleCreate = async () => {
    const name = newName.trim() || pageTitle?.trim();
    if (!name) {
      toast.error('Enter a menu label');
      return;
    }
    if (typeof rootMenid !== 'number') {
      toast.error('Pick a footer column / menu root');
      return;
    }
    try {
      const node = await createMutation.mutateAsync({
        name,
        menid_root: rootMenid,
        menid_parent: typeof parentMenid === 'number' ? parentMenid : rootMenid,
        cmsoid,
        active: 1,
      });
      // Ensure link even if create ignored cmsoid for some reason
      if (node?.menid && node.cmsoid !== cmsoid) {
        await linkMutation.mutateAsync({ cmsoid, menid: node.menid });
      } else {
        await qc.invalidateQueries({ queryKey: cmsKeys.pageMenus(cmsoid) });
      }
      toast.success(`Created “${name}” under ${rootLabel(rootMenid)}`);
      setNewName('');
      setMode('linked');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to create menu node');
    }
  };

  const busy =
    linkMutation.isPending || unlinkMutation.isPending || createMutation.isPending;

  return (
    <div className={className}>
      <div className="mb-3 flex gap-1 rounded-md border border-gray-200 bg-gray-50 p-0.5">
        {(
          [
            ['linked', 'Linked', linked.length],
            ['link', 'Link existing', null],
            ['create', 'Create link', null],
          ] as const
        ).map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            onClick={() => setMode(id)}
            className={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1.5 text-xs font-medium ${
              mode === id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {id === 'linked' && <Link2 className="h-3.5 w-3.5" />}
            {id === 'link' && <Search className="h-3.5 w-3.5" />}
            {id === 'create' && <Plus className="h-3.5 w-3.5" />}
            {label}
            {count != null && count > 0 && (
              <span className="text-gray-400">({count})</span>
            )}
          </button>
        ))}
      </div>

      {mode === 'linked' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            Nav nodes that deep-link here resolve to this page’s slug once published.
          </p>
          {menusQuery.isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}
          {!menusQuery.isLoading && linked.length === 0 && (
            <div className="rounded-md border border-dashed border-gray-200 px-3 py-6 text-center">
              <p className="text-xs text-gray-500">Not in any menu yet.</p>
              <div className="mt-3 flex justify-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setMode('link')}>
                  Link existing
                </Button>
                <Button size="sm" onClick={() => setMode('create')}>
                  Create link
                </Button>
              </div>
            </div>
          )}
          <ul className="max-h-72 space-y-2 overflow-y-auto">
            {linked.map((m) => (
              <li
                key={m.menid}
                className="flex items-start justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {m.name || `Menu #${m.menid}`}
                  </p>
                  <p className="mt-0.5 text-[11px] text-gray-500">
                    {rootLabel(m.menid_root)} · #{m.menid}
                    {m.active === false && (
                      <span className="ml-1 text-amber-600">inactive</span>
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Link
                    to="/admin/navigation"
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                    title="Open navigation admin"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                  <button
                    type="button"
                    className="rounded p-1.5 text-red-600 hover:bg-red-50"
                    title="Unlink"
                    disabled={busy}
                    onClick={() => handleUnlink(m.menid, m.name)}
                  >
                    <Unlink className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {mode === 'link' && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            Search any nav node, or browse a footer column and click Link.
          </p>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search menu nodes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {search.trim().length >= 2 && (
            <ul className="max-h-40 space-y-1 overflow-y-auto rounded-md border">
              {searchQuery.isLoading && (
                <li className="px-3 py-2 text-xs text-gray-400">Searching…</li>
              )}
              {!searchQuery.isLoading && (searchQuery.data?.items ?? []).length === 0 && (
                <li className="px-3 py-2 text-xs text-gray-400">No matches</li>
              )}
              {(searchQuery.data?.items ?? []).map((n) => {
                const already = linkedIds.has(n.menid);
                return (
                  <li
                    key={n.menid}
                    className="flex items-center justify-between gap-2 border-b px-3 py-2 last:border-0"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm text-gray-900">{n.name}</p>
                      <p className="text-[11px] text-gray-400">
                        {rootLabel(n.menid_root)} · #{n.menid}
                        {n.cmsoid != null && n.cmsoid !== cmsoid && (
                          <span className="ml-1 text-amber-600">→ cms #{n.cmsoid}</span>
                        )}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant={already ? 'ghost' : 'outline'}
                      className="h-7 shrink-0"
                      disabled={busy || already}
                      onClick={() => handleLink(n.menid, n.name)}
                    >
                      {already ? <Check className="h-3.5 w-3.5" /> : 'Link'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
              <FolderTree className="h-3 w-3" /> Browse footer column
            </label>
            <select
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
              value={rootMenid}
              onChange={(e) => {
                setRootMenid(e.target.value ? Number(e.target.value) : '');
                setParentMenid('');
              }}
            >
              {(rootsQuery.data?.length
                ? rootsQuery.data
                : FOOTER_MENU_ROOTS.map((r) => ({ menid: r.menid, name: r.label }))
              ).map((r) => (
                <option key={r.menid} value={r.menid}>
                  #{r.menid} — {FOOTER_MENU_ROOTS.find((f) => f.menid === r.menid)?.label ?? r.name}
                </option>
              ))}
            </select>
          </div>

          <ul className="max-h-56 space-y-0.5 overflow-y-auto rounded-md border">
            {treeQuery.isLoading && (
              <li className="px-3 py-2 text-xs text-gray-400">Loading tree…</li>
            )}
            {!treeQuery.isLoading && flatTree.length === 0 && (
              <li className="px-3 py-2 text-xs text-gray-400">No nodes in this column</li>
            )}
            {flatTree.map(({ node, depth }) => {
              const already = linkedIds.has(node.menid);
              return (
                <li
                  key={node.menid}
                  className="flex items-center justify-between gap-2 border-b px-2 py-1.5 last:border-0"
                  style={{ paddingLeft: 8 + depth * 12 }}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-gray-900">{node.name}</p>
                    <p className="text-[11px] text-gray-400">#{node.menid}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={already ? 'ghost' : 'outline'}
                    className="h-7 shrink-0"
                    disabled={busy || already}
                    onClick={() => handleLink(node.menid, node.name)}
                  >
                    {already ? <Check className="h-3.5 w-3.5" /> : 'Link'}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {mode === 'create' && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            Add a new nav link under a footer column and point it at this page.
          </p>
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
              Label
            </label>
            <Input
              placeholder={pageTitle || 'Menu label'}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
              Footer column
            </label>
            <select
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
              value={rootMenid}
              onChange={(e) => {
                setRootMenid(e.target.value ? Number(e.target.value) : '');
                setParentMenid('');
              }}
            >
              {FOOTER_MENU_ROOTS.map((r) => (
                <option key={r.menid} value={r.menid}>
                  #{r.menid} — {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
              Parent (optional — defaults to column root)
            </label>
            <select
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
              value={parentMenid}
              onChange={(e) => setParentMenid(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Column root</option>
              {flatTree.map(({ node, depth }) => (
                <option key={node.menid} value={node.menid}>
                  {'· '.repeat(depth)}
                  {node.name} (#{node.menid})
                </option>
              ))}
            </select>
          </div>
          <Button className="w-full gap-2" disabled={busy} onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            Create & link
          </Button>
          <Link
            to="/admin/navigation"
            className="block text-center text-xs text-blue-600 hover:underline"
          >
            Open full navigation admin
          </Link>
        </div>
      )}
    </div>
  );
};

export default CMSMenuLinksPanel;
